import React from 'react';

// Atwater Energy — Blue Ridge Hazier palette
// Cooler, more fog — surfaces shift from warm to blue-gray
export const REPORT = {
  heading: '#2E3840',       // near-ridge — headings, primary text
  body: '#3E4E5C',          // dark-ridge — body text (darker for readability on cool bg)
  secondary: '#6A7E8E',     // far-ridge — descriptions
  tertiary: '#788894',      // between far and distant — hints, sources
  rule: 'rgba(46,56,64,0.08)',  // border-light
  surface: '#D8DCE0',       // cool mist — card surfaces, panels, infra/env cards
  border: 'rgba(46,56,64,0.10)', // border-light subtle
  white: '#E4E6EA',         // haze — page background (cooler, bluer)
  accent: '#C4705A',        // sunrise — section labels, links
  highlight: '#506272',     // mid-ridge — section rules (softer than dark-ridge)
  sand: '#CCD2D6',          // cool overcast — pills, deeper surfaces
  // Status
  pass: '#5C6B45',
  passWash: '#DCE0D8',
  passText: '#4A5838',
  caution: '#9A7B30',
  cautionWash: '#E0DACA',
  cautionText: '#7A6020',
  fail: '#A04030',
  failWash: '#E4D6D0',
  failText: '#8A302A',
  // Extras
  violet: '#6A7E8E',        // far-ridge — metadata, sources
  amberWash: '#F0E4DE',     // sunrise-wash — callout backgrounds
  patina: '#D49880',        // sunrise-light — brand text on dark bgs
  // Tier eyebrow inks — single-channel, never used as a card bg
  tierOk: '#6e7e44',        // favorable / olive
  tierWarn: '#9a7320',      // caution / ochre (also: resolvable risk)
  tierInfo: '#4f555c',      // info / neutral gray (also: open product question)
  tierFail: '#A04030',      // existential / red — matches REPORT.fail (Blue Ridge palette)
} as const;

export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    fontSize: '10px', fontWeight: 700, color: REPORT.heading,
    textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px',
    borderBottom: `2px solid ${REPORT.highlight}`, paddingBottom: '4px',
  }}>
    {children}
  </div>
);

export const InfoRow: React.FC<{ label: string; value: string | number | null | undefined; source?: string }> = ({ label, value, source }) => {
  if (value == null || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', padding: '2px 0', gap: '8px' }}>
      <span style={{ color: REPORT.secondary, flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, color: REPORT.heading, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
        {source && <span style={{ fontSize: '8px', color: REPORT.tertiary, marginLeft: '4px' }}>({source})</span>}
      </span>
    </div>
  );
};

export const SourceFootnote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: '9px', color: REPORT.violet, marginTop: '4px' }}>
    {children}
  </div>
);

// Magazine-kicker eyebrow: monospace label with a 2px tier-colored underline.
// Replaces side-stripe + filled-chip tier signaling on briefing item cards.
export const TierEyebrow: React.FC<{ color: string; children: React.ReactNode; marginBottom?: number | string }> = ({ color, children, marginBottom = 8 }) => (
  <span style={{
    display: 'inline-block',
    fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '10px',
    letterSpacing: '0.13em',
    textTransform: 'uppercase',
    fontWeight: 600,
    color,
    borderBottom: `2px solid ${color}`,
    padding: '0 0 3px',
    marginBottom,
    lineHeight: 1.2,
  }}>
    {children}
  </span>
);
